import { Controller } from "@hotwired/stimulus"
import { writing } from "../lib/ways"

// The business screen (accounts/businesses/show): the top of a paper beside the fields, redrawn as they're typed, and
// a first guess at the country and the currency from the browser's language ("en-AU"), never from where it connects.
// The currency field becomes the papers' own picker (CurrencyPicker); without this, it's a plain box for a sign.
export default class extends Controller {
  static targets = ["name", "phone", "email", "currency", "picker", "country", "guessed", "file", "drop", "dropImg", "dropName",
    "pName", "pReach", "pLogo", "amount", "paper"]
  static values = { guessPaper: Boolean }

  async connect() {
    this.ownCurrency = Boolean(this.currencyTarget.value)
    const guessed = !this.countryTarget.value && this.guessCountry()
    this.draw()
    const picker = await import("../components/mountCurrencyPicker")
    this.signFor = picker.signFor
    if (!this.ownCurrency) this.guessCurrency()
    this.guessPaper()
    if (guessed || (!this.ownCurrency && this.currencyTarget.value)) this.guessedTarget.hidden = false
    // The picker takes the box's place and its label; the box stays, hidden, to send what's picked.
    const box = this.currencyTarget
    this.picker = picker.mount(this.pickerTarget, {
      id: box.id, label: "Currency", className: "w-full",
      onChange: (c) => { box.value = c; this.ownCurrency = true; this.paint(); this.draw() }
    })
    box.removeAttribute("id")
    box.type = "hidden"
    this.paint = () => this.picker.draw(box.value)
    this.paint()
    this.draw()
  }

  disconnect() {
    this.picker?.unmount()
    if (this.url) URL.revokeObjectURL(this.url)
  }

  // The first country the browser's languages name that's in the list.
  guessCountry() {
    for (const tag of navigator.languages ?? [navigator.language]) {
      let region
      try { region = new Intl.Locale(tag).region } catch { continue }
      if (region && [...this.countryTarget.options].some((o) => o.value === region)) {
        this.countryTarget.value = region
        return true
      }
    }
    return false
  }

  guessCurrency() {
    const code = this.countryTarget.selectedOptions[0]?.dataset.currency
    if (!code || !this.signFor) return
    this.currencyTarget.value = this.signFor(code, this.countryTarget.value)
    this.paint?.()
  }

  // A new business's paper follows its country (A4, or US Letter where that's what prints), until the owner picks one.
  guessPaper() {
    const paper = this.countryTarget.selectedOptions[0]?.dataset.paper
    if (this.guessPaperValue && paper && this.hasPaperTarget) this.paperTarget.value = paper
  }

  ownPaper() {
    this.guessPaperValue = false
  }

  // A new country brings its currency, until the owner picks one.
  country() {
    if (!this.ownCurrency) this.guessCurrency()
    this.guessPaper()
    this.draw()
  }

  // A picked logo shows in the box and on the paper at once; nothing is sent until Save. Cleared, the box gets its
  // picture back.
  logo() {
    if (!this.hasFileTarget) return
    const file = this.fileTarget.files[0]
    if (this.url) URL.revokeObjectURL(this.url)
    this.url = file ? URL.createObjectURL(file) : null
    this.icon ??= [...this.dropImgTarget.childNodes]
    this.dropTarget.classList.toggle("is-picked", Boolean(file))
    this.dropNameTarget.textContent = file ? file.name : ""
    const img = () => (this.url ? [Object.assign(document.createElement("img"), { src: this.url, alt: "" })] : [])
    this.dropImgTarget.replaceChildren(...(this.url ? img() : this.icon))
    this.pLogoTarget.replaceChildren(...img())
  }

  over() { this.dropTarget.classList.add("is-over") }
  out() { this.dropTarget.classList.remove("is-over") }

  draw() {
    this.pNameTarget.textContent = this.nameTarget.value.trim() || "Your business"
    // The first time, only the name and the money are asked.
    const reach = [this.hasPhoneTarget && this.phoneTarget, this.hasEmailTarget && this.emailTarget].filter(Boolean)
    this.pReachTarget.textContent = reach.map((el) => el.value.trim()).filter(Boolean).join(" · ")
    const w = writing({ currency: this.currencyTarget.value })
    for (const el of this.amountTargets) el.textContent = w.money(Number(el.dataset.cents))
  }
}
